/**
 * Area drawer component
 * Map with drawing tools for selecting area
 */

import { useState, useCallback } from 'react';
import { Typography, Space, Alert } from 'antd';
import { MapContainer } from '@/components/maps/MapContainer';
import { DrawControls } from '@/components/maps/DrawControls';
import { BoundsOverlay } from '@/components/maps/GridOverlay';
import type { Bounds } from '@/types/grid';
import { MAP_DEFAULTS } from '@/utils/constants';

const { Text } = Typography;

interface AreaDrawerProps {
  bounds?: Bounds;
  onBoundsChange: (bounds: Bounds) => void;
  height?: number;
}

export function AreaDrawer({ bounds, onBoundsChange, height = 350 }: AreaDrawerProps) {
  const [drawMode, setDrawMode] = useState<'draw' | 'view'>('view');

  const handleBoundsChange = useCallback(
    (newBounds: Bounds) => {
      onBoundsChange(newBounds);
      setDrawMode('view');
    },
    [onBoundsChange]
  );

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Click and drag on the map to draw a rectangle for the search area.
      </Text>

      <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
        <MapContainer
          height={height}
          center={MAP_DEFAULTS.center}
          zoom={MAP_DEFAULTS.initialZoom}
        >
          <DrawControls
            initialBounds={bounds}
            onBoundsChange={handleBoundsChange}
            mode={drawMode}
            onModeChange={setDrawMode}
          />

          {bounds && drawMode === 'view' && (
            <BoundsOverlay bounds={bounds} />
          )}
        </MapContainer>
      </div>

      {!bounds && (
        <Alert
          type="info"
          message="Draw a rectangle on the map to select the search area"
          style={{ marginTop: 12 }}
          showIcon
        />
      )}

      {bounds && (
        <div style={{ marginTop: 12 }}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">North:</Text>
              <Text>{bounds.north.toFixed(6)}°</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">South:</Text>
              <Text>{bounds.south.toFixed(6)}°</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">East:</Text>
              <Text>{bounds.east.toFixed(6)}°</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">West:</Text>
              <Text>{bounds.west.toFixed(6)}°</Text>
            </div>
          </Space>
        </div>
      )}
    </div>
  );
}

export default AreaDrawer;
